import clsx from 'clsx';
import { CSSProperties, memo, PropsWithChildren, ReactNode, useCallback, useRef, useState } from 'react';
import { useResizeObserver } from 'src/modules/common/hooks/useResizeObserver.ts';

type Props = PropsWithChildren<{
    title: ReactNode;
    value: ReactNode;
    direction?: 'top' | 'bottom';
    disabled?: boolean;
    showContent?: boolean;
}>

export const Tooltip = memo(({
    title,
    value,
    direction = 'bottom',
    disabled,
    showContent = true,
    children,
}: Props) => {
    const [contentShift, setContentShift] = useState('0px');
    const contentRef = useRef<HTMLDivElement>(null);
    const arrowRef = useRef<HTMLDivElement>(null);

    const handleDropdownPosition = useCallback(() => {
        if (!contentRef.current || !arrowRef.current) {
            return;
        }

        const contentRect = contentRef.current.getBoundingClientRect();
        const arrowRect = arrowRef.current.getBoundingClientRect();

        const leftEdgeDistance = arrowRect.left - (contentRect.width / 2);
        const rightEdgeDistance = window.innerWidth - arrowRect.right - (contentRect.width / 2);

        if (leftEdgeDistance < 0) {
            const shift = Math.abs(leftEdgeDistance);

            setContentShift(`${shift}px`);
        } else if (rightEdgeDistance < 0) {
            const shift = rightEdgeDistance;

            setContentShift(`${shift}px`);
        } else {
            setContentShift('0px');
        }
    }, []);

    useResizeObserver([contentRef, arrowRef], handleDropdownPosition);

    return (
        <div className="vss-tooltip">
            <div className={clsx({
                'vss-tooltip__target': true,
                'vss-tooltip__target--disabled': disabled,
            })}
            >
                {children}
            </div>

            {showContent && (
                <div
                    ref={arrowRef}
                    className={clsx({
                        'vss-tooltip__arrow': true,
                        'vss-tooltip__arrow--disabled': disabled,
                        'vss-tooltip__arrow--top': direction === 'top',
                    })}
                />
            )}

            {showContent && (
                <div
                    className={clsx({
                        'vss-tooltip__content': true,
                        'vss-tooltip__content--disabled': disabled,
                        'vss-tooltip__content--top': direction === 'top',
                    })}
                    ref={contentRef}
                    style={{ '--vss-tooltip-content-shift': contentShift } as CSSProperties}
                >
                    <div className="vss-tooltip__title">{title}</div>

                    {value && (
                        <div className="vss-tooltip__value">{value}</div>
                    )}
                </div>
            )}
        </div>
    );
});
